import { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../../context';
import { canAccess } from '../../utils/rbac';
import { isHardwarePendingReview } from '../../utils/navCounts';
import api from '../../hooks/useApi';
import { formatMoney, formatDate, safeUrl } from '../../utils/format';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';
import SowSelect from '../shared/SowSelect';
import FilterCombo from '../shared/FilterCombo';
import { prefillFromCatalogue } from '../../utils/hardwareRequestPrefill';
import ProductSupplyNote from './ProductSupplyNote';
import CatalogueSuppliers from './CatalogueSuppliers';
import { noteAge, isOutOfDate } from '../../utils/productNotes';

const STATUS_LABELS = {
  submitted:      'Submitted',
  under_review:   'Under Review',
  approved:       'Approved',
  rejected:       'Rejected',
  po_created:     'PO Created',
  incoming_stock: 'Stock Incoming',
  part_delivered: 'Part Delivered',
  completed:      'Completed',
};

const STATUS_COLORS = {
  submitted:      'bg-blue-100 text-blue-700',
  under_review:   'bg-amber-100 text-amber-700',
  approved:       'bg-emerald-100 text-emerald-700',
  rejected:       'bg-red-100 text-red-600',
  po_created:     'bg-navy/10 text-navy',
  incoming_stock: 'bg-amber-100 text-amber-800',
  part_delivered: 'bg-amber-100 text-amber-900',
  completed:      'bg-emerald-100 text-emerald-800',
};

// Statuses that are still "in flight" — show in the Active tab
const ACTIVE_STATUSES = new Set(['submitted', 'under_review', 'approved', 'po_created', 'incoming_stock', 'part_delivered']);

// Awaiting a PMO decision: what the header's "pending review" badge counts,
// and the sidebar's (utils/navCounts).
const isPendingReview = isHardwarePendingReview;

const NO_FILTERS = { product: '', requester: '', supplier: '', sow: '', po: '' };

// A PO is raised to one supplier, so grouping keys off the supplier record where
// there is one and the proposed name otherwise.
const supplierKey = (r) => r.supplier_id ? `id:${r.supplier_id}`
  : (r.supplier_name_free ? `free:${r.supplier_name_free.trim().toLowerCase()}` : 'none');

// A PO is also raised against one SOW. A request with no SOW is its own bucket,
// so it cannot be folded in beside one that has one.
const sowKey = (r) => r.sow_id ? `id:${r.sow_id}` : 'none';

// Each filter is a free-text box: `match` is the text a row is matched against as
// you type, `label` is how the row appears in that box's suggestion list. They
// differ where searching wider than the label helps — a SOW is worth finding by
// its description, not only its reference.
const FILTER_FIELDS = {
  product: {
    label: r => r.product_name || '',
    match: r => [r.product_name, r.part_number, r.catalog_code].filter(Boolean).join(' '),
  },
  requester: {
    label: r => r.requester_name || '',
    match: r => r.requester_name || '',
  },
  supplier: {
    label: r => r.supplier_name || r.supplier_name_free || 'No supplier set',
    match: r => r.supplier_name || r.supplier_name_free || 'No supplier set',
  },
  sow: {
    label: r => r.sow_reference || r.sow_reason || 'No SOW or reason',
    match: r => [r.sow_reference, r.sow_detail, r.sow_reason].filter(Boolean).join(' ') || 'No SOW or reason',
  },
  po: {
    label: r => r.linked_po_number || '',
    match: r => r.linked_po_number || '',
  },
};

const contains = (haystack, needle) => String(haystack || '').toLowerCase().includes(needle.trim().toLowerCase());

// Distinct suggestions for one filter, from the rows on screen, so the list only
// ever offers values that are actually there.
const suggestionsFor = (rows, field) =>
  [...new Set(rows.map(FILTER_FIELDS[field].label).filter(Boolean))].sort((a, b) => a.localeCompare(b));

function StatusBadge({ status }) {
  return (
    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-500'}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

const PO_STATUS_LABELS = { draft: 'Draft', issued: 'Issued', acknowledged: 'Acknowledged', part_delivered: 'Part Delivered', delivered: 'Delivered', closed: 'Closed', cancelled: 'Cancelled' };
const PO_STATUS_COLORS = { draft: 'bg-gray-100 text-gray-600', issued: 'bg-blue-100 text-blue-700', acknowledged: 'bg-sky-100 text-sky-700', part_delivered: 'bg-amber-100 text-amber-700', delivered: 'bg-emerald-100 text-emerald-700', closed: 'bg-navy/10 text-navy', cancelled: 'bg-red-100 text-red-600' };

export default function HardwareRequestPage() {
  const { toast } = useToast();
  const { currentUser, navigate, detailId } = useApp();
  const [requests, setRequests]     = useState([]);
  const [showNew, setShowNew]       = useState(false);
  const [reviewReq, setReviewReq]   = useState(null);
  const [viewReq, setViewReq]       = useState(null);
  const [tab, setTab]               = useState('active');   // 'active' | 'completed' | 'archived'
  const [viewMine, setViewMine]     = useState(false);
  const [filters, setFilters]       = useState(NO_FILTERS);
  const [pendingOnly, setPendingOnly] = useState(false);  // header badge narrows to requests awaiting review
  const [selected, setSelected]     = useState(() => new Set());  // request ids picked for a combined PO

  // Who can review/action requests (sees the pending-review badge, the Review/
  // Approve/Create-PO actions and the "My requests only" toggle): whoever holds
  // the review key in Admin → Access, the same key the API asks, so a team
  // given it there sees the reviewer view without a deploy.
  const isPMO = canAccess('hardware-requests:review');
  // Deleting somebody else's request is its own key. The requester may always
  // delete their own while it is Submitted.
  const canDeleteAny = canAccess('hardware-requests:delete');
  const canViewPOs = canAccess('pos');

  // The Archived tab is its own list: archived requests never appear in the
  // Active or Completed views, so the server is asked for one set or the other.
  const load = useCallback(() => {
    const params = {};
    if (viewMine) params.my = '1';
    if (tab === 'archived') params.archived = '1';
    api.get('hardware-requests', params).then(setRequests).catch(() => setRequests([]));
  }, [viewMine, tab]);

  useEffect(load, [load]);

  // Auto-open a specific request when navigated here with a detailId — e.g.
  // from the notification bell or the PMO submission email deep link.
  useEffect(() => {
    if (!detailId || requests.length === 0) return;
    const r = requests.find(req => req.id === detailId);
    if (!r) return;
    if (isPMO && ['submitted', 'under_review'].includes(r.status)) setReviewReq(r);
    else setViewReq(r);
  }, [detailId, requests, isPMO]);

  const submit = async (data) => {
    await api.post('hardware-requests', data);
    setShowNew(false);
    load();
  };

  const updateStatus = async (id, status, pmo_notes, stock_checked) => {
    await api.put(`hardware-requests/${id}`, { status, pmo_notes, stock_checked });
    setReviewReq(null);
    load();
  };

  const createPO = async (id) => {
    if (!confirm('This will create a Draft PO for this request and mark it as PO Created. Continue?')) return;
    const res = await api.post(`hardware-requests/${id}/create-po`, {}).catch(e => { toast.error('Failed: ' + e.message); return null; });
    if (res?.po_number) {
      toast.success(`PO ${res.po_number} created successfully as Draft. Click the PO number in the table to open it.`);
      load();
    }
  };

  // Combine several approved requests (same supplier) into one Draft PO, one line
  // per request — e.g. three requests all going to TD Synnex ordered together.
  const createBulkPO = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!confirm(`Combine ${ids.length} approved request${ids.length > 1 ? 's' : ''} into a single Draft PO?`)) return;
    const res = await api.post('hardware-requests/create-po-bulk', { ids }).catch(e => { toast.error('Failed: ' + e.message); return null; });
    if (res?.po_number) {
      toast.success(`PO ${res.po_number} created with ${ids.length} line${ids.length > 1 ? 's' : ''} as Draft.`);
      setSelected(new Set());
      load();
    }
  };

  // Combined action from Review modal: save approved status + create PO in one step.
  // A refused approval throws, so the dialog can say why beside its buttons.
  const approveAndCreatePO = async (id, notes, stock_checked) => {
    // 1. Save the approved status (with any PMO notes and the stock check)
    await api.put(`hardware-requests/${id}`, { status: 'approved', pmo_notes: notes || '', stock_checked });
    // 2. Close the modal immediately
    setReviewReq(null);
    // 3. Create the PO (no extra confirm — the button is the confirmation)
    const res = await api.post(`hardware-requests/${id}/create-po`, {}).catch(e => { toast.error('Failed to create PO: ' + e.message); return null; });
    if (res?.po_number) {
      toast.success(`Approved. PO ${res.po_number} created as Draft.`);
    }
    load();
  };

  const editReq = async (id, data) => {
    await api.put(`hardware-requests/${id}`, data);
    setViewReq(null);
    load();
  };

  const deleteReq = async (id) => {
    if (!confirm('Delete this request?')) return;
    await api.delete(`hardware-requests/${id}`).catch(e => toast.error(e.message));
    load();
  };

  // Archiving hides a request from the Active and Completed tabs without deleting
  // it. Anything can be archived, whatever its status; Restore puts it back.
  const archiveReq = async (id) => {
    if (!confirm('Archive this request? It moves to the Archived tab and can be restored from there.')) return;
    await api.post(`hardware-requests/${id}/archive`, {}).catch(e => toast.error(e.message));
    load();
  };

  const restoreReq = async (id) => {
    await api.post(`hardware-requests/${id}/restore`, {}).catch(e => toast.error(e.message));
    toast.success('Request restored.');
    load();
  };

  const canArchive = (r) => isPMO || r.requested_by_id === currentUser?.id;

  const onArchivedTab     = tab === 'archived';
  const activeRequests    = onArchivedTab ? [] : requests.filter(r => ACTIVE_STATUSES.has(r.status));
  const completedRequests = onArchivedTab ? [] : requests.filter(r => !ACTIVE_STATUSES.has(r.status));
  const tabRequests       = onArchivedTab ? requests : tab === 'active' ? activeRequests : completedRequests;
  const pending           = requests.filter(isPendingReview).length;

  // The badge opens the pending requests where they live, on the Active tab.
  const showPending = () => {
    setTab('active');
    setPendingOnly(p => !p);
  };
  useEffect(() => { if (tab !== 'active') setPendingOnly(false); }, [tab]);

  const productOpts   = suggestionsFor(tabRequests, 'product');
  const requesterOpts = suggestionsFor(tabRequests, 'requester');
  const supplierOpts  = suggestionsFor(tabRequests, 'supplier');
  const sowOpts       = suggestionsFor(tabRequests, 'sow');
  const poOpts        = suggestionsFor(tabRequests, 'po');

  const activeFilters = Object.values(filters).filter(v => v.trim()).length;
  const displayed = tabRequests.filter(r =>
    (!pendingOnly || isPendingReview(r)) &&
    Object.entries(filters).every(([field, text]) => !text.trim() || contains(FILTER_FIELDS[field].match(r), text))
  );

  // A request can join a combined PO once the PMO has approved it and it isn't
  // already tied to a PO. Selection lives only on the Active tab.
  const canSelectForPO = (r) => isPMO && !onArchivedTab && r.status === 'approved' && !r.po_id;
  const selectable     = displayed.filter(canSelectForPO);
  const selectedRows   = requests.filter(r => selected.has(r.id));

  // The PMO can complete missing details (usually the supplier) on anyone's
  // request up until a PO exists — past that the PO is the record and edits here
  // would not carry through to it. Requesters may only correct their own request
  // while it is still sitting in Submitted.
  const canEditRequest = (r) => !onArchivedTab && (isPMO
    ? !r.po_id
    : (r.requested_by_id === currentUser?.id && r.status === 'submitted'));

  // A PO is raised to one supplier; flag when the picked requests span several so
  // the user knows to split them (the backend enforces this too). A request with
  // no supplier at all counts as its own bucket, which is the usual reason a set
  // that belongs on one PO refuses to group.
  const selectedSuppliers = [...new Set(selectedRows.map(supplierKey))];
  const mixedSuppliers    = selectedSuppliers.length > 1;
  const missingSupplier   = selectedRows.some(r => supplierKey(r) === 'none');

  // Likewise for the SOW: goods bought on a PO inherit that PO's SOW, so a PO
  // spanning two would deliver kit that belongs to a job into unallocated stock.
  // Name the SOWs that clashed, because the fix is to split the selection along
  // them and "different SOWs" alone does not say where the line falls.
  const mixedSows = [...new Set(selectedRows.map(sowKey))].length > 1;
  const clashingSows = mixedSows
    ? [...new Set(selectedRows.map(r => r.sow_reference || 'no SOW'))].join(', ')
    : '';
  const cannotCombine = mixedSuppliers || mixedSows;

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allSelected = selectable.length > 0 && selectable.every(r => selected.has(r.id));
  const toggleSelectAll = () => setSelected(prev => {
    if (selectable.every(r => prev.has(r.id))) {
      const next = new Set(prev);
      selectable.forEach(r => next.delete(r.id));
      return next;
    }
    return new Set([...prev, ...selectable.map(r => r.id)]);
  });

  // Reset the selection whenever the view changes so stale picks can't linger —
  // including on a filter change, otherwise a row scrolled out of view could
  // still end up on a combined PO.
  useEffect(() => { setSelected(prev => (prev.size ? new Set() : prev)); }, [tab, viewMine, filters, pendingOnly]);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Hardware Requests</h2>
          <div className="text-[9px] text-gray-500">
            Submit requests to the ATV PMO team to procure hardware
            {isPMO && pending > 0 && (
              <button
                onClick={showPending}
                aria-pressed={pendingOnly}
                title={pendingOnly ? 'Show all requests' : 'Show only the requests awaiting review'}
                className={`ml-2 font-bold px-1.5 py-0.5 rounded text-[8px] border hover:opacity-80 ${pendingOnly ? 'bg-amber-200 text-amber-800 border-amber-400' : 'bg-amber-100 text-amber-700 border-transparent'}`}
              >{pendingOnly ? '✕ ' : ''}{pending} pending review</button>
            )}
          </div>
        </div>
        <button onClick={() => setShowNew(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">
          + New Request
        </button>
      </div>

      {/* Tabs + filters */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-0 border border-gray-200 rounded overflow-hidden">
          {[['active', 'Active', onArchivedTab ? null : activeRequests.length],
            ['completed', 'Completed', onArchivedTab ? null : completedRequests.length],
            ['archived', 'Archived', onArchivedTab ? requests.length : null]].map(([key, label, count]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-3 py-1.5 text-[10px] font-semibold flex items-center gap-1.5 transition-colors ${
                tab === key ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
              {count !== null && (
                <span className={`text-[8px] font-bold px-1 py-0.5 rounded-full ${
                  tab === key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                }`}>{count}</span>
              )}
            </button>
          ))}
        </div>
        {!isPMO ? null : (
          <label className="flex items-center gap-1 text-[9px] text-gray-600 cursor-pointer">
            <input type="checkbox" checked={viewMine} onChange={e => setViewMine(e.target.checked)} className="w-3 h-3" />
            My requests only
          </label>
        )}
      </div>

      {/* Column filters — type to narrow, or pick from the values present */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[8px] font-bold text-gray-400 uppercase">Filter</span>
        <FilterCombo
          value={filters.product}
          onChange={v => setFilters(f => ({ ...f, product: v }))}
          options={productOpts}
          placeholder="Product…"
          width={190}
        />
        <FilterCombo
          value={filters.requester}
          onChange={v => setFilters(f => ({ ...f, requester: v }))}
          options={requesterOpts}
          placeholder="Requested by…"
        />
        <FilterCombo
          value={filters.supplier}
          onChange={v => setFilters(f => ({ ...f, supplier: v }))}
          options={supplierOpts}
          placeholder="Supplier…"
        />
        <FilterCombo
          value={filters.sow}
          onChange={v => setFilters(f => ({ ...f, sow: v }))}
          options={sowOpts}
          placeholder="SOW / reason…"
          width={190}
        />
        {poOpts.length > 0 && (
          <FilterCombo
            value={filters.po}
            onChange={v => setFilters(f => ({ ...f, po: v }))}
            options={poOpts}
            placeholder="PO reference…"
          />
        )}
        {activeFilters > 0 && (
          <>
            <button
              onClick={() => setFilters(NO_FILTERS)}
              className="text-[9px] px-2 py-1 border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50"
            >
              Clear filters
            </button>
            <span className="text-[9px] text-gray-500">
              Showing {displayed.length} of {tabRequests.length}
            </span>
          </>
        )}
      </div>

      {/* Bulk create-PO bar — appears once one or more approved requests are picked */}
      {isPMO && selected.size > 0 && (
        <div className="flex items-center justify-between mb-3 px-3 py-2 bg-navy/5 border border-navy/20 rounded">
          <div className="text-[10px] text-navy">
            <span className="font-bold">{selected.size}</span> request{selected.size > 1 ? 's' : ''} selected
            {mixedSuppliers && (
              <span className="ml-2 text-amber-700 font-semibold">
                {missingSupplier
                  ? 'One or more of these has no supplier set. Use Edit to add it, then they can go on one PO.'
                  : 'Different suppliers selected, create one PO per supplier.'}
              </span>
            )}
            {mixedSows && !mixedSuppliers && (
              <span className="ml-2 text-amber-700 font-semibold">
                A PO covers one SOW. These span {clashingSows}, so create a separate PO for each.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="text-[9px] px-2 py-1 border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={createBulkPO}
              disabled={cannotCombine}
              className="text-[10px] px-3 py-1 font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40"
              title={mixedSuppliers ? 'All selected requests must share the same supplier'
                   : mixedSows ? 'All selected requests must be for the same SOW'
                   : 'Create one Draft PO covering the selected requests'}
            >
              Create PO for {selected.size} request{selected.size > 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              {isPMO && (
                <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b w-6">
                  <input
                    type="checkbox"
                    className="w-3 h-3 align-middle"
                    checked={allSelected}
                    disabled={selectable.length === 0}
                    onChange={toggleSelectAll}
                    title="Select all approved requests awaiting a PO"
                  />
                </th>
              )}
              {['Product', 'SOW / Reason', 'Qty', 'Unit Cost', 'Total', 'Supplier', 'Requested By', 'Status', 'PMO Notes', ''].map(h => (
                <th key={h} className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.map(r => (
              <tr key={r.id} className={`border-b border-gray-100 hover:bg-gold/5 ${selected.has(r.id) ? 'bg-navy/5' : ''}`}>
                {isPMO && (
                  <td className="px-2 py-1 text-center">
                    {canSelectForPO(r) ? (
                      <input
                        type="checkbox"
                        className="w-3 h-3 align-middle"
                        checked={selected.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                      />
                    ) : null}
                  </td>
                )}
                <td className="px-2 py-1">
                  <div className="text-[10px] font-semibold">{r.product_name}</div>
                  {r.catalog_code && <div className="text-[8px] font-mono text-navy">{r.catalog_code}</div>}
                  {r.part_number && <div className="text-[8px] text-gray-400">PN: {r.part_number}</div>}
                  {r.product_url && <a href={safeUrl(r.product_url)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[8px] text-blue-600 hover:underline">🔗 Product link</a>}
                </td>
                <td className="px-2 py-1">
                  {r.sow_reference ? (
                    <div>
                      <div className="text-[9px] font-semibold text-navy">{r.sow_reference}</div>
                      {r.sow_detail && <div className="text-[8px] text-gray-400 truncate max-w-[140px]">{r.sow_detail}</div>}
                    </div>
                  ) : (
                    <div className="text-[9px] text-gray-500 italic">{r.sow_reason || '—'}</div>
                  )}
                  {r.notes && (
                    <div className="text-[8px] text-gray-500 mt-0.5 max-w-[160px]" title={r.notes}>
                      <span className="font-semibold text-gray-400">Note: </span>{r.notes}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1 text-[10px] text-center font-semibold">{r.quantity}</td>
                {/* The price is trade cost: without trade-cost:see, review or Purchase Orders the API
                    sends it on your own requests only (pen test AD26341, AD12/AD16), so a missing
                    price is left blank rather than shown as none. */}
                <td className="px-2 py-1 text-[10px] text-right">{r.unit_cost === undefined ? '' : r.unit_cost != null ? formatMoney(r.unit_cost) : '—'}</td>
                <td className="px-2 py-1 text-[10px] text-right font-bold">{r.total_cost === undefined ? '' : r.total_cost != null ? formatMoney(r.total_cost) : '—'}</td>
                <td className="px-2 py-1 text-[9px]">{r.supplier_name || r.supplier_name_free || <span className="text-gray-300">Unknown</span>}</td>
                <td className="px-2 py-1 text-[9px] text-gray-600">
                  <div>{r.requester_name}</div>
                  <div className="text-[7px] text-gray-400">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : ''}</div>
                  {r.required_by_date && (
                    <div className={`text-[7px] font-semibold ${r.required_by_date < new Date().toISOString().split('T')[0] ? 'text-red-500' : 'text-amber-600'}`}>
                      Needed: {formatDate(r.required_by_date)}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1">
                  <StatusBadge status={r.status} />
                  {r.po_id && (
                    <div className="mt-0.5">
                      {canViewPOs ? (
                        <span
                          className="text-[7px] text-navy font-semibold cursor-pointer hover:underline"
                          onClick={() => navigate('pos', r.po_id)}
                          title="Open this Purchase Order"
                        >
                          → {r.linked_po_number || 'View PO'}
                        </span>
                      ) : (
                        <span className="text-[7px] text-gray-400 font-semibold">
                          → {r.linked_po_number || 'PO Created'}
                        </span>
                      )}
                      {r.linked_po_status && (
                        <span className={`ml-1 text-[7px] font-bold px-1 py-0.5 rounded ${PO_STATUS_COLORS[r.linked_po_status] || 'bg-gray-100 text-gray-500'}`}>
                          {PO_STATUS_LABELS[r.linked_po_status] || r.linked_po_status}
                        </span>
                      )}
                      {/* The PO's own status covers every request combined onto
                          it, so say how much of this request has actually come
                          in. Hidden once there is nothing left to wait for. */}
                      {r.stock_total > 0 && r.stock_received < r.stock_total && (
                        <div className="text-[7px] text-gray-500 mt-0.5">
                          {r.stock_received} of {r.stock_total} received
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1 text-[9px] text-gray-500 max-w-[180px]">
                  {r.pmo_notes || <span className="text-gray-300">—</span>}
                  {r.reviewer_name && <div className="text-[7px] text-gray-400 mt-0.5">By {r.reviewer_name}</div>}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-center">
                  {/* Requester: view / edit their own request. PMO: view or fill
                      in the gaps on anyone's, so requests can be grouped onto
                      one PO without going back to the requester. */}
                  {(r.requested_by_id === currentUser?.id || isPMO) && (
                    <button
                      onClick={() => setViewReq(r)}
                      className="text-[8px] px-1.5 py-0.5 bg-gray-100 text-gray-600 font-semibold rounded hover:bg-gray-200 mr-1"
                    >
                      {canEditRequest(r) ? 'Edit' : 'View'}
                    </button>
                  )}
                  {/* PMO workflow actions */}
                  {isPMO && !onArchivedTab && ['submitted','under_review'].includes(r.status) && (
                    <button
                      onClick={() => setReviewReq(r)}
                      className="text-[8px] px-1.5 py-0.5 bg-navy/10 text-navy font-semibold rounded hover:bg-navy/20 mr-1"
                    >
                      Review
                    </button>
                  )}
                  {isPMO && !onArchivedTab && r.status === 'approved' && !r.po_id && (
                    <button
                      onClick={() => createPO(r.id)}
                      className="text-[8px] px-1.5 py-0.5 bg-navy text-white font-semibold rounded hover:bg-navy/80 mr-1"
                    >
                      Create PO
                    </button>
                  )}
                  {onArchivedTab ? (
                    canArchive(r) && (
                      <button
                        onClick={() => restoreReq(r.id)}
                        className="text-[8px] px-1.5 py-0.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50 font-semibold mr-1"
                      >
                        Restore
                      </button>
                    )
                  ) : (
                    canArchive(r) && (
                      <button
                        onClick={() => archiveReq(r.id)}
                        className="text-[8px] px-1.5 py-0.5 border border-gray-200 rounded text-gray-500 hover:bg-gray-50 mr-1"
                        title="Hide this request. It moves to the Archived tab and can be restored."
                      >
                        Archive
                      </button>
                    )
                  )}
                  {(r.requested_by_id === currentUser?.id || canDeleteAny) && !onArchivedTab && r.status === 'submitted' && (
                    <button onClick={() => deleteReq(r.id)} className="text-[8px] text-red-400 hover:text-red-600 font-bold" title="Delete this request">×</button>
                  )}
                </td>
              </tr>
            ))}
            {displayed.length === 0 && (
              <tr>
                <td colSpan={isPMO ? 11 : 10} className="text-center py-8 text-[10px] text-gray-400">
                  {pendingOnly
                    ? 'No requests are awaiting review.'
                    : tab === 'active'
                    ? (viewMine ? 'You have no active requests.' : 'No active requests at the moment.')
                    : tab === 'completed'
                    ? (viewMine ? 'You have no completed requests.' : 'No completed requests yet.')
                    : (viewMine ? 'You have no archived requests.' : 'No archived requests.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewRequestModal onClose={() => setShowNew(false)} onSubmit={submit} />
      )}

      {viewReq && (
        <ViewEditModal
          request={viewReq}
          canEdit={canEditRequest(viewReq)}
          isPMO={isPMO}
          isOwn={viewReq.requested_by_id === currentUser?.id}
          onClose={() => setViewReq(null)}
          onSave={editReq}
        />
      )}

      {reviewReq && (
        <ReviewModal
          request={reviewReq}
          onClose={() => setReviewReq(null)}
          onSave={updateStatus}
          onCreatePO={createPO}
          onApproveAndCreatePO={approveAndCreatePO}
          navigate={navigate}
        />
      )}

    </div>
  );
}

// ── NEW REQUEST MODAL ─────────────────────────────────────────────────────────
function NewRequestModal({ onClose, onSubmit }) {
  const [sows, setSows]           = useState([]);
  const [catalog, setCatalog]     = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [catSearch, setCatSearch] = useState('');
  // The product's supply note as the form is showing it, recorded on the
  // request so the PMO can see what the requester was told.
  const [noteShown, setNoteShown] = useState(null);
  // The picked product's catalogue suppliers, listed under it, and the Product
  // Link the pick filled in, so picking another product clears a link that
  // came from the last one but keeps one the requester typed.
  const [catSuppliers, setCatSuppliers] = useState(null);
  const autoUrl = useRef('');
  const [form, setForm] = useState({
    sow_id: '', sow_reason: '',
    product_catalog_id: '', product_name: '', part_number: '', product_url: '',
    quantity: 1, unit_cost: '', supplier_id: '', supplier_name_free: '',
    notes: '', required_by_date: '', delivery_address_id: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    Promise.all([
      api.get('sows').catch(() => []),
      api.get('product-catalog').catch(() => []),
      api.get('suppliers').catch(() => []),
      api.get('delivery-addresses').catch(() => []),
    ]).then(([s, c, sup, da]) => {
      setSows(s); setCatalog(c); setSuppliers(sup); setDeliveryAddresses(da);
      // Pre-select the default delivery address, if one is flagged.
      const def = (da || []).find(a => a.is_default);
      if (def) setForm(p => ({ ...p, delivery_address_id: def.id }));
    });
  }, []);

  const activeSows = sows.filter(s => !['closed','cancelled'].includes(s.status));

  const filteredCatalog = catSearch
    ? catalog.filter(c =>
        c.description?.toLowerCase().includes(catSearch.toLowerCase()) ||
        c.code?.toLowerCase().includes(catSearch.toLowerCase())
      )
    : catalog.slice(0, 40);

  const selectedCat = form.product_catalog_id ? catalog.find(c => c.id === form.product_catalog_id) : null;

  const handleCatSelect = (item) => {
    // Always overwrite — so switching product replaces all linked fields
    setForm(p => ({
      ...p,
      product_catalog_id: item.id,
      product_name: item.description,
      part_number: item.code || '',
      unit_cost: item.unit_cost != null ? String(item.unit_cost) : '',
      product_url: autoUrl.current && p.product_url === autoUrl.current ? '' : p.product_url,
    }));
    autoUrl.current = '';
    setCatSuppliers(null);
    setCatSearch('');
    // The list endpoint carries no supplier detail, so fetch the item to pull
    // its preferred supplier, that supplier's price and, when it is the only
    // supplier, its link to the item into the request.
    // Guard against a slow reply landing after a different item was picked.
    api.get(`product-catalog/${item.id}`)
      .then(full => {
        const fill = prefillFromCatalogue(full, full?.suppliers, suppliers);
        setForm(p => {
          if (p.product_catalog_id !== item.id) return p;
          if (fill.product_url) autoUrl.current = fill.product_url;
          return { ...p, ...fill };
        });
        setCatSuppliers(full?.suppliers || []);
      })
      .catch(() => {});
  };

  const handleCatClear = () => {
    setForm(p => ({
      ...p, product_catalog_id: '', product_name: '', part_number: '', unit_cost: '',
      product_url: autoUrl.current && p.product_url === autoUrl.current ? '' : p.product_url,
    }));
    autoUrl.current = '';
    setCatSuppliers(null);
  };

  // Work out exactly what the submit gate is still waiting on, so a disabled
  // button can say why instead of just sitting grey. The Reason field is the
  // usual culprit: it only appears when no SOW is linked, and it is easy to
  // read as optional when the Notes box below already takes a justification.
  const missing = [];
  if (!form.product_name.trim()) missing.push('Product Name');
  if (!form.sow_id && !form.sow_reason.trim()) missing.push('a linked SOW, or a Reason');
  if (!(parseInt(form.quantity) >= 1)) missing.push('Quantity (1 or more)');
  // The PMO will not chase a price or work out where to buy from: every
  // request carries a unit cost and either a link to the item or a supplier.
  if (!(parseFloat(form.unit_cost) > 0)) missing.push('Unit Cost');
  if (!form.product_url.trim() && !form.supplier_id && !form.supplier_name_free.trim()) missing.push('a Product Link, or a Supplier');
  const valid = missing.length === 0;

  const totalCost = (parseFloat(form.unit_cost) || 0) * (parseInt(form.quantity) || 1);

  return (
    <Modal title="New Hardware Request" onClose={onClose} wide footer={
      <>
        {!valid && (
          <div className="mr-auto self-center text-[9px] text-amber-700 font-semibold">
            Still needed: {missing.join(' · ')}
          </div>
        )}
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button
          onClick={() => onSubmit({ ...form, quantity: parseInt(form.quantity) || 1, unit_cost: parseFloat(form.unit_cost) || null, product_note_id_seen: noteShown?.id || null })}
          disabled={!valid}
          className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
        >
          Submit Request
        </button>
      </>
    }>
      <div className="grid grid-cols-2 gap-3">
        {/* SOW selection */}
        <div className="col-span-2 border border-gray-200 rounded p-2 bg-gray-50">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">
            Link to SOW * <span className="font-normal text-gray-400">(or provide a reason if not project-specific)</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <SowSelect
                sows={activeSows}
                value={form.sow_id}
                onChange={id => { set('sow_id', id); if (id) set('sow_reason', ''); }}
                placeholder="— Not linked to a SOW —"
              />
            </div>
            {!form.sow_id && (
              <div>
                <input
                  className={`form-input ${!form.sow_reason.trim() ? 'border-amber-400 bg-amber-50/40' : ''}`}
                  placeholder="Reason * (e.g. Lab equipment, Office upgrade...)"
                  value={form.sow_reason}
                  onChange={e => set('sow_reason', e.target.value)}
                />
                {!form.sow_reason.trim() && (
                  <div className="text-[8px] text-amber-700 mt-0.5">
                    Required unless you link a SOW. The PMO uses it on the PO.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Product catalogue search */}
        <div className="col-span-2 border border-gray-200 rounded p-2 bg-gray-50">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">
            Product Catalogue <span className="font-normal text-gray-400">(search to link, or enter manually below)</span>
          </label>
          {selectedCat ? (
            <div className="flex items-center justify-between bg-gold/10 border border-gold/20 rounded px-2 py-1">
              <div>
                <span className="text-[8px] font-mono text-navy mr-2">{selectedCat.code}</span>
                <span className="text-[9px] font-semibold">{selectedCat.description}</span>
                {selectedCat.manufacturer && <span className="text-[8px] text-gray-400 ml-2">{selectedCat.manufacturer}</span>}
              </div>
              <button onClick={handleCatClear} className="text-[8px] text-red-400 hover:text-red-600 ml-2">✕ Clear</button>
            </div>
          ) : (
            <input
              className="form-input"
              placeholder="🔍 Search by name or code..."
              value={catSearch}
              onChange={e => setCatSearch(e.target.value)}
            />
          )}
          {!selectedCat && catSearch && (
            <div className="border border-gray-200 rounded max-h-[120px] overflow-y-auto mt-1 bg-white">
              {filteredCatalog.map(c => (
                <div
                  key={c.id}
                  onClick={() => handleCatSelect(c)}
                  className="px-2 py-1 border-b border-gray-100 cursor-pointer hover:bg-gold/5 flex justify-between items-center"
                >
                  <div>
                    <span className="text-[8px] font-mono text-navy mr-2">{c.code}</span>
                    <span className="text-[9px]">{c.description}</span>
                    {c.manufacturer && <span className="text-[8px] text-gray-400 ml-2">{c.manufacturer}</span>}
                  </div>
                  {c.unit_cost != null && <span className="text-[8px] text-gray-500 ml-2">{formatMoney(c.unit_cost)}</span>}
                </div>
              ))}
              {filteredCatalog.length === 0 && <div className="text-center py-2 text-[9px] text-gray-400">No matches — fill in manually below</div>}
            </div>
          )}
          {selectedCat && (
            <div className="mt-1.5">
              <ProductSupplyNote productId={selectedCat.id} canWrite={canAccess('product-notes:write')} requesting onCurrent={setNoteShown} />
            </div>
          )}
          {selectedCat && catSuppliers?.length > 0 && (
            <div className="mt-1.5">
              <CatalogueSuppliers suppliers={catSuppliers} />
            </div>
          )}
        </div>

        {/* Product detail */}
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Product Name *</label>
          <input
            className="form-input"
            placeholder="e.g. Cisco Catalyst 9300-48P"
            value={form.product_name}
            onChange={e => set('product_name', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Part Number</label>
          <input className="form-input" value={form.part_number} onChange={e => set('part_number', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Product Link <span className="font-normal text-gray-400">(a page showing exactly what to order; required unless you give a supplier)</span></label>
          <input className="form-input" type="url" placeholder="https://…" value={form.product_url} onChange={e => set('product_url', e.target.value)} />
          {autoUrl.current && form.product_url === autoUrl.current && (
            <div className="text-[8px] text-gray-500 mt-0.5">Filled in from the catalogue supplier's link.</div>
          )}
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Quantity *</label>
          <input className="form-input" type="number" min="1" value={form.quantity} onChange={e => set('quantity', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Unit Cost (£) *</label>
          <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.unit_cost} onChange={e => set('unit_cost', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">
            Total Cost
          </label>
          <div className="form-input bg-gray-50 text-[10px] font-bold text-navy">{totalCost > 0 ? formatMoney(totalCost) : '—'}</div>
        </div>

        {/* Supplier */}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Supplier <span className="font-normal text-gray-400">(required unless you give a product link)</span></label>
          <select className="form-select" value={form.supplier_id} onChange={e => { set('supplier_id', e.target.value); if (e.target.value) set('supplier_name_free', ''); }}>
            <option value="">— Select supplier —</option>
            {suppliers.filter(s => s.is_active !== 0).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        {!form.supplier_id && (
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Or propose a new supplier</label>
            <input className="form-input" placeholder="e.g. Westcoast, Ingram Micro..." value={form.supplier_name_free} onChange={e => set('supplier_name_free', e.target.value)} />
            {form.supplier_name_free.trim() && (
              <div className="text-[8px] text-amber-700 mt-0.5">Proposed only — the PMO reviews this, and no supplier record is created unless they order from them.</div>
            )}
          </div>
        )}

        {/* Required by date + Delivery location */}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Hardware Required By <span className="font-normal text-gray-400">(date needed)</span></label>
          <input className="form-input" type="date" value={form.required_by_date} onChange={e => set('required_by_date', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Delivery Location <span className="font-normal text-gray-400">(where to ship)</span></label>
          <select className="form-select" value={form.delivery_address_id} onChange={e => set('delivery_address_id', e.target.value)}>
            <option value="">— Select delivery location —</option>
            {deliveryAddresses.map(a => (
              <option key={a.id} value={a.id}>{a.name}{a.city ? ` — ${a.city}` : ''}{a.postcode ? ` (${a.postcode})` : ''}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes / Justification</label>
          <textarea className="form-textarea" rows="2" placeholder="Any additional context for the PMO team..." value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

// ── REQUESTER VIEW / EDIT MODAL ───────────────────────────────────────────────
function ViewEditModal({ request, canEdit, isPMO, isOwn, onClose, onSave }) {
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({
    product_name:        request.product_name || '',
    part_number:         request.part_number  || '',
    product_url:         request.product_url  || '',
    quantity:            request.quantity     || 1,
    unit_cost:           request.unit_cost    != null ? String(request.unit_cost) : '',
    supplier_id:         request.supplier_id  || '',
    supplier_name_free:  request.supplier_name_free || '',
    notes:               request.notes        || '',
    delivery_address_id: request.delivery_address_id || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const totalCost = (parseFloat(form.unit_cost) || 0) * (parseInt(form.quantity) || 1);

  const selectableSuppliers = suppliers.filter(s => s.is_active !== 0);
  const keepCurrentSupplier = form.supplier_id && !selectableSuppliers.some(s => s.id === form.supplier_id);

  useEffect(() => {
    if (!canEdit) return;
    api.get('delivery-addresses').then(setDeliveryAddresses).catch(() => setDeliveryAddresses([]));
    api.get('suppliers').then(setSuppliers).catch(() => setSuppliers([]));
  }, [canEdit]);

  return (
    <Modal
      title={canEdit ? `Edit Request: ${request.product_name}` : `Request: ${request.product_name}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">
            {canEdit ? 'Cancel' : 'Close'}
          </button>
          {canEdit && (
            <button
              onClick={() => onSave(request.id, {
                product_name:       form.product_name.trim(),
                part_number:        form.part_number.trim(),
                product_url:        form.product_url.trim(),
                quantity:            parseInt(form.quantity) || 1,
                unit_cost:           parseFloat(form.unit_cost) || null,
                supplier_id:         form.supplier_id || null,
                // A picked supplier record wins — keeping the free-text name too
                // would leave two competing answers on the same request.
                supplier_name_free:  form.supplier_id ? '' : form.supplier_name_free.trim(),
                notes:               form.notes.trim(),
                delivery_address_id: form.delivery_address_id || null,
              })}
              disabled={!form.product_name.trim()}
              className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
            >
              Save Changes
            </button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        {/* Status banner */}
        <div className="flex items-center gap-2">
          <StatusBadge status={request.status} />
          {!canEdit && (
            <span className="text-[8px] text-gray-400 italic">
              {request.po_id
                ? 'A PO has been raised from this request, so it can no longer be edited.'
                : 'This request is under review and can no longer be edited.'}
            </span>
          )}
          {canEdit && isPMO && !isOwn && (
            <span className="text-[8px] text-navy italic">
              Editing {request.requester_name || 'another user'}&rsquo;s request as PMO.
            </span>
          )}
        </div>

        {/* SOW / reason — read-only */}
        <div className="bg-gray-50 border border-gray-200 rounded p-2">
          <div className="text-[8px] font-bold text-gray-500 uppercase mb-0.5">SOW / Reason</div>
          <div className="text-[9px]">
            {request.sow_reference
              ? <><span className="font-semibold text-navy">{request.sow_reference}</span>{request.sow_detail && <span className="text-gray-500 ml-1">— {request.sow_detail}</span>}</>
              : <span className="text-gray-500">{request.sow_reason || '—'}</span>
            }
          </div>
        </div>

        {/* Editable fields */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Product Name *</label>
            {canEdit
              ? <input className="form-input" value={form.product_name} onChange={e => set('product_name', e.target.value)} />
              : <div className="form-input bg-gray-50 text-[10px]">{form.product_name}</div>
            }
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Part Number</label>
            {canEdit
              ? <input className="form-input" value={form.part_number} onChange={e => set('part_number', e.target.value)} />
              : <div className="form-input bg-gray-50 text-[10px]">{form.part_number || '—'}</div>
            }
          </div>
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Product Link</label>
            {canEdit
              ? <input className="form-input" type="url" placeholder="https://…" value={form.product_url} onChange={e => set('product_url', e.target.value)} />
              : (form.product_url
                  ? <a href={safeUrl(form.product_url)} target="_blank" rel="noopener noreferrer" className="form-input bg-gray-50 text-[10px] text-blue-700 hover:underline block truncate">{form.product_url}</a>
                  : <div className="form-input bg-gray-50 text-[10px]">—</div>)
            }
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Quantity</label>
            {canEdit
              ? <input className="form-input" type="number" min="1" value={form.quantity} onChange={e => set('quantity', e.target.value)} />
              : <div className="form-input bg-gray-50 text-[10px]">{form.quantity}</div>
            }
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Unit Cost (£)</label>
            {canEdit
              ? <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.unit_cost} onChange={e => set('unit_cost', e.target.value)} />
              : <div className="form-input bg-gray-50 text-[10px]">{form.unit_cost ? formatMoney(parseFloat(form.unit_cost)) : '—'}</div>
            }
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Total Cost</label>
            <div className="form-input bg-gray-50 text-[10px] font-bold text-navy">{totalCost > 0 ? formatMoney(totalCost) : '—'}</div>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Supplier</label>
            {canEdit
              ? <select className="form-select" value={form.supplier_id} onChange={e => { set('supplier_id', e.target.value); if (e.target.value) set('supplier_name_free', ''); }}>
                  <option value="">— Select supplier —</option>
                  {/* Keep the supplier already on the request selectable even if it
                      is now inactive, or the list has yet to load, so an existing
                      choice never reads as unset. */}
                  {keepCurrentSupplier && <option value={form.supplier_id}>{request.supplier_name || 'Current supplier'}</option>}
                  {selectableSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              : <div className="form-input bg-gray-50 text-[10px]">{request.supplier_name || form.supplier_name_free || '—'}</div>
            }
            {canEdit && !form.supplier_id && (
              <input
                className="form-input mt-1"
                placeholder="Or propose a supplier not on the list..."
                value={form.supplier_name_free}
                onChange={e => set('supplier_name_free', e.target.value)}
              />
            )}
            {canEdit && !form.supplier_id && !form.supplier_name_free.trim() && (
              <div className="text-[8px] text-amber-700 mt-0.5">
                Requests with no supplier cannot be combined onto one PO.
              </div>
            )}
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Delivery Location</label>
            {canEdit
              ? <select className="form-select" value={form.delivery_address_id} onChange={e => set('delivery_address_id', e.target.value)}>
                  <option value="">— Select delivery location —</option>
                  {deliveryAddresses.map(a => (
                    <option key={a.id} value={a.id}>{a.name}{a.city ? ` — ${a.city}` : ''}{a.postcode ? ` (${a.postcode})` : ''}</option>
                  ))}
                </select>
              : <div className="form-input bg-gray-50 text-[10px]">{request.delivery_address_name || '—'}</div>
            }
          </div>
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes / Justification</label>
            {canEdit
              ? <textarea className="form-textarea" rows="2" value={form.notes} onChange={e => set('notes', e.target.value)} />
              : <div className="form-input bg-gray-50 text-[10px] min-h-[40px]">{form.notes || '—'}</div>
            }
          </div>
        </div>

        {/* The product's supply note and stock as they stand now */}
        {request.product_catalog_id && (
          <ProductSupplyNote productId={request.product_catalog_id} canWrite={canAccess('product-notes:write')} />
        )}
        <StockCheckRecord request={request} />

        {/* PMO notes — always read-only for requester */}
        {request.pmo_notes && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2">
            <div className="text-[8px] font-bold text-amber-600 uppercase mb-0.5">PMO Notes</div>
            <div className="text-[9px] text-amber-800">{request.pmo_notes}</div>
            {request.reviewer_name && <div className="text-[7px] text-amber-500 mt-0.5">By {request.reviewer_name}</div>}
          </div>
        )}
      </div>
    </Modal>
  );
}

// The supply note the requester was shown when they asked, with its age as it
// stood then: what they were told, not what the note says now.
function SeenNote({ request }) {
  if (!request.seen_note_body) return null;
  const age = noteAge({ created_at: request.seen_note_created_at, valid_until: request.seen_note_valid_until }, new Date(request.created_at));
  return (
    <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1.5" data-testid="seen-supply-note">
      <div className="text-[8px] font-bold text-gray-500 uppercase">
        Supply note the requester was shown
        <span className={`ml-2 normal-case font-semibold ${isOutOfDate(age) ? 'text-red-700' : 'text-gray-400'}`}>
          {age.days === 0 ? 'logged that day' : `${age.days} day${age.days === 1 ? '' : 's'} old at the time`}{age.state === 'expired' ? ', already expired' : age.state === 'stale' ? ', already out of date' : ''}
        </span>
      </div>
      <div className="text-[9px] text-gray-700 my-0.5 whitespace-pre-wrap">{request.seen_note_body}</div>
      <div className="text-[8px] text-gray-400">
        Logged by {request.seen_note_by || 'unknown'} on {formatDate(request.seen_note_created_at)}
        {request.seen_note_valid_until && <> · valid until {formatDate(request.seen_note_valid_until)}</>}
      </div>
    </div>
  );
}

// Who ticked the stock check when the request was approved with stock showing.
function StockCheckRecord({ request }) {
  if (!request.stock_checked_at) return null;
  return (
    <div className="text-[8.5px] text-gray-500" data-testid="stock-check-recorded">
      ✓ {request.stock_checked_by_name || 'A reviewer'} checked with the inventory manager on {formatDate(request.stock_checked_at)}
      {request.stock_checked_qty ? ` (${request.stock_checked_qty} in stock then)` : ''} and still needed to order it.
    </div>
  );
}

// ── PMO REVIEW MODAL ──────────────────────────────────────────────────────────
function ReviewModal({ request, onClose, onSave, onCreatePO, onApproveAndCreatePO, navigate }) {
  const [status, setStatus] = useState(request.status);
  const [notes, setNotes]   = useState(request.pmo_notes || '');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');
  const [inStock, setInStock]           = useState(0);
  const [stockChecked, setStockChecked] = useState(false);

  const alreadyApproved = request.status === 'approved';
  const willBeApproved  = status === 'approved';
  const showCreatePO    = (alreadyApproved || willBeApproved) && !request.po_id;
  // Approving is the decision to order, so while stock is showing it waits on
  // the reviewer saying they checked with the inventory manager. The server
  // refuses it too, and records who ticked it.
  const stockCheckOwed  = willBeApproved && !alreadyApproved && inStock > 0 && !request.stock_checked_at;
  const blocked         = stockCheckOwed && !stockChecked;

  const run = async (action) => {
    setBusy(true); setError('');
    try {
      await action();
    } catch (e) {
      setError(e.message || 'The request was not saved.');
    } finally {
      setBusy(false);
    }
  };
  const handleApproveAndCreate = () => run(() => onApproveAndCreatePO(request.id, notes, stockChecked));
  const handleSave = () => run(() => onSave(request.id, status, notes, stockChecked));

  const today = new Date().toISOString().split('T')[0];
  const isOverdue = request.required_by_date && request.required_by_date < today;

  return (
    <Modal title={`Review: ${request.product_name}`} onClose={onClose} wide footer={
      <>
        {(blocked || error) && (
          <div className={`mr-auto self-center text-[9px] font-semibold ${error ? 'text-red-700' : 'text-amber-700'}`}>
            {error || 'Still needed: tick the stock check before approving'}
          </div>
        )}
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        {showCreatePO && (
          <button
            onClick={alreadyApproved ? () => { onClose(); onCreatePO(request.id); } : handleApproveAndCreate}
            disabled={busy || blocked}
            className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50"
          >
            {busy ? 'Creating PO...' : alreadyApproved ? 'Create PO' : 'Approve & Create PO'}
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={busy || blocked}
          className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50"
        >
          Save
        </button>
      </>
    }>
      <div className="space-y-3">
        {/* Request summary */}
        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[9px] grid grid-cols-2 gap-1.5">
          <div>
            <span className="font-bold text-gray-500">Product: </span>
            <span className="font-semibold">{request.product_name}</span>
            {request.catalog_code && <span className="font-mono text-navy ml-1 text-[8px]">({request.catalog_code})</span>}
            {request.product_catalog_id && navigate && (
              <button onClick={() => { onClose(); navigate('product-catalog'); }} className="ml-1.5 text-[7px] text-blue-500 hover:underline">[View catalogue]</button>
            )}
          </div>
          {request.part_number && <div><span className="font-bold text-gray-500">Part no:</span> {request.part_number}</div>}
          {request.product_url && (
            <div className="col-span-2">
              <span className="font-bold text-gray-500">Link: </span>
              <a href={safeUrl(request.product_url)} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline break-all">{request.product_url}</a>
            </div>
          )}
          <div><span className="font-bold text-gray-500">Qty:</span> {request.quantity}</div>
          {request.unit_cost != null && <div><span className="font-bold text-gray-500">Unit Cost:</span> {formatMoney(request.unit_cost)}</div>}
          {request.total_cost != null && <div><span className="font-bold text-gray-500">Total:</span> <span className="font-bold text-navy">{formatMoney(request.total_cost)}</span></div>}
          <div>
            <span className="font-bold text-gray-500">SOW: </span>
            {request.sow_reference
              ? <><span className="font-semibold text-navy">{request.sow_reference}</span>{request.sow_detail && <span className="text-gray-400"> — {request.sow_detail}</span>}</>
              : <span>{request.sow_reason || 'None'}</span>}
            {request.sow_id && navigate && (
              <button onClick={() => { onClose(); navigate('tracker'); }} className="ml-1.5 text-[7px] text-blue-500 hover:underline">[View SOW]</button>
            )}
          </div>
          <div>
            <span className="font-bold text-gray-500">Supplier: </span>
            {request.supplier_name || request.supplier_name_free || 'Not specified'}
            {!request.supplier_id && request.supplier_name_free && <span className="text-amber-700 font-semibold ml-1">(proposed new — approve before ordering)</span>}
            {request.supplier_id && navigate && (
              <button onClick={() => { onClose(); navigate('suppliers'); }} className="ml-1.5 text-[7px] text-blue-500 hover:underline">[View supplier]</button>
            )}
          </div>
          <div><span className="font-bold text-gray-500">Delivery to: </span>{request.delivery_address_name || <span className="text-gray-400">Not specified</span>}</div>
          <div><span className="font-bold text-gray-500">Requested by:</span> {request.requester_name}</div>
          <div><span className="font-bold text-gray-500">Date requested:</span> {request.created_at ? new Date(request.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</div>
          {request.required_by_date && (
            <div className={`col-span-2 px-2 py-1 rounded ${isOverdue ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-amber-50 border border-amber-100 text-amber-700'}`}>
              <span className="font-bold">{isOverdue ? '⚠ OVERDUE — ' : ''}Required by:</span> {formatDate(request.required_by_date)}
            </div>
          )}
          {request.notes && <div className="col-span-2"><span className="font-bold text-gray-500">Notes:</span> {request.notes}</div>}
        </div>

        {/* Where the catalogue says this product can be bought, and how */}
        {request.product_catalog_id && (
          <CatalogueSuppliers productId={request.product_catalog_id} onRequest={request.supplier_name || request.supplier_name_free} />
        )}

        {/* Supply: what the requester was told, then the note and stock as they stand now */}
        <SeenNote request={request} />
        {request.product_catalog_id && (
          <ProductSupplyNote productId={request.product_catalog_id} canWrite={canAccess('product-notes:write')} onStock={setInStock} />
        )}
        <StockCheckRecord request={request} />

        {/* Status */}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Update Status</label>
          <select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
            {/* po_created, incoming_stock and part_delivered are set by the PO
                and its deliveries, never chosen by hand. */}
            {Object.entries(STATUS_LABELS).filter(([k]) => !['po_created', 'incoming_stock', 'part_delivered'].includes(k)).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        {/* PMO Notes */}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">PMO Notes</label>
          <textarea
            className="form-textarea"
            rows="2"
            placeholder="Approval notes, rejection reason, queries..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>

        {stockCheckOwed && (
          <label className={`flex items-start gap-2 rounded border px-2 py-1.5 cursor-pointer ${stockChecked ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-200'}`} data-testid="stock-check">
            <input type="checkbox" checked={stockChecked} onChange={e => setStockChecked(e.target.checked)} className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span className="text-[9px] text-gray-800">
              <span className="font-bold">{inStock} in stock.</span> I checked with the inventory manager if we have this in stock and need to order it anyway. <span className="text-amber-700 font-semibold">Required to approve.</span>
            </span>
          </label>
        )}

        {status === 'approved' && !request.po_id && (
          <div className="text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
            ✓ Once approved, use <strong>Approve & Create PO</strong> to automatically raise a Draft Purchase Order.
          </div>
        )}
        {status === 'rejected' && (
          <div className="text-[9px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            Please provide a reason in the PMO Notes field.
          </div>
        )}
      </div>
    </Modal>
  );
}
